import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './scss/childAccessSettings.scss';
import api from './services/apiService';
import { normalizeUkMobile, validateUkMobile } from './utils/phoneUtils';

import { useNotification } from './components/NotificationProvider';

// Styles for specific elements
const styles = {
  toggleDisabledMessage: {
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#fff3cd',
    borderRadius: '4px',
    border: '1px solid #ffeaa7',
    color: '#856404',
    fontSize: '14px'
  }
};

function ParentSettings() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [permission, setPermission] = useState(null);
  const [allowChildAccess, setAllowChildAccess] = useState(false);
  const [allowChildAvailability, setAllowChildAvailability] = useState(false);
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [childMobileNumber, setChildMobileNumber] = useState('');
  const [childPassword, setChildPassword] = useState('');
  const [childName, setChildName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null); // Add this state for API response
  const [isParent, setIsParent] = useState(false);
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);

  useEffect(() => {
    // Check if user is a parent
    const userType = sessionStorage.getItem('user_type');
    const parentFlag = sessionStorage.getItem('is_parent');
    const isParentUser = userType === 'parent' || parentFlag === 'true';
    
    if (!isParentUser) {
      navigate('/dashboard');
      return;
    }

    setIsParent(true);
    fetchParentSettings();
  }, [navigate]);

  const fetchParentSettings = async () => {
    try {
      // For parent accounts, user_id in sessionStorage contains the parent_id
      const parentId = sessionStorage.getItem('user_id');
      const playerId = sessionStorage.getItem('player_id');
      
      if (!playerId) {
        showError('Player ID not found. Please try logging in again.');
        return;
      }
      
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_child_access_permissions.php?parent_id=${parentId}&player_id=${playerId}`);
      
      if (response.data.success) {
        setApiResponse(response); // Store the full API response
        setPermission(response.data.permission);
        
        // Set toggle based on whether the child has active access permission
        // This should reflect the actual access state, not just approval status
        const hasActiveAccess = response.data.permission?.is_active || false;
        setAllowChildAccess(hasActiveAccess);
        setAllowChildAvailability(
          hasActiveAccess && (response.data.permission?.can_respond_to_availability || false)
        );
        
        setChildMobileNumber(response.data.permission?.child_mobile_number || '');
        setChildPassword(''); // Don't populate password for security
        if (response.data.permission?.child_name) {
          setChildName(response.data.permission.child_name);
        }
      }
    } catch (error) {
      console.error('Error fetching child access permissions:', error);
      showError('Error loading child access settings. Please try again.');
    }
  };



  const handleToggleChange = () => {
    if (showApprovalForm) {
      handleCancelApproval();
      return;
    }

    if (allowChildAccess) {
      setShowDisableConfirmation(true);
      return;
    }

    setShowApprovalForm(true);
    setAllowChildAccess(true);
  };

  const handleConfirmedDisable = async () => {
    setShowDisableConfirmation(false);
    await handleDisableAccess();
  };

  const handleDisableAccess = async () => {
    setIsLoading(true);
    try {
      const parentId = sessionStorage.getItem('user_id');
      const playerId = sessionStorage.getItem('player_id');
      
      if (!playerId) {
        showError('Player ID not found. Please try logging in again.');
        return;
      }
      
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_child_access_permissions.php`, {
        parent_id: parentId,
        player_id: playerId,
        allow_child_access: false,
        child_mobile_number: ''
      });

      if (response.data.success) {
        setAllowChildAccess(false);
        setAllowChildAvailability(false);
        setShowApprovalForm(false);
        setChildMobileNumber('');
        
        // Update the permission data with the response
        if (response.data.permission) {
          setPermission(response.data.permission);
        }
        
        showSuccess('Child access disabled successfully');
      } else {
        showError(response.data.message || 'Failed to disable access');
      }
    } catch (error) {
      console.error('Error disabling child access:', error);
      showError('An error occurred while disabling access');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveAccess = async () => {
    if (!childMobileNumber.trim()) {
      showError('Please enter your child\'s mobile number');
      return;
    }

    if (!validateUkMobile(childMobileNumber)) {
      showError('Please enter a valid UK mobile number (e.g. 07700 900123)');
      return;
    }

    if (!childPassword.trim()) {
      showError('Please enter a password for your child');
      return;
    }

    if (childPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      const parentId = sessionStorage.getItem('user_id');
      const playerId = sessionStorage.getItem('player_id');
      
      if (!playerId) {
        showError('Player ID not found. Please try logging in again.');
        return;
      }
      
      const normalizedMobile = normalizeUkMobile(childMobileNumber);

      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_child_access_permissions.php`, {
        parent_id: parentId,
        player_id: playerId,
        allow_child_access: true,
        child_mobile_number: normalizedMobile,
        child_password: childPassword,
        can_respond_to_availability: allowChildAvailability
      });

      if (response.data.success) {
        setAllowChildAccess(true);
        setShowApprovalForm(false);
        setChildPassword(''); // Clear password for security
        
        // Update the permission data with the response
        if (response.data.permission) {
          setPermission(response.data.permission);
          setChildMobileNumber(response.data.permission.child_mobile_number || '');
          setAllowChildAvailability(response.data.permission.can_respond_to_availability || false);
        }
        
        showSuccess('Child access enabled successfully');
      } else {
        showError(response.data.message || 'Failed to enable access');
      }
    } catch (error) {
      console.error('Error enabling child access:', error);
      showError('An error occurred while enabling access');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelApproval = () => {
    setShowApprovalForm(false);
    setChildMobileNumber('');
    setChildPassword('');
    const hasActiveAccess = apiResponse?.data?.permission?.is_active || false;
    setAllowChildAccess(hasActiveAccess);
    setAllowChildAvailability(
      hasActiveAccess && (apiResponse?.data?.permission?.can_respond_to_availability || false)
    );
  };

  const handleMobileNumberChange = (e) => {
    setChildMobileNumber(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setChildPassword(e.target.value);
  };

  const handleAvailabilityToggleChange = async () => {
    const newValue = !allowChildAvailability;

    // During initial app access setup, save locally until parent approves access
    if (showApprovalForm) {
      setAllowChildAvailability(newValue);
      return;
    }

    setIsLoading(true);
    try {
      const parentId = sessionStorage.getItem('user_id');
      const playerId = sessionStorage.getItem('player_id');

      if (!playerId) {
        showError('Player ID not found. Please try logging in again.');
        return;
      }

      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_child_access_permissions.php`, {
        parent_id: parentId,
        player_id: playerId,
        availability_only: 1,
        can_respond_to_availability: newValue
      });

      if (response.data.success) {
        setAllowChildAvailability(
          response.data.permission?.can_respond_to_availability ?? newValue
        );
        if (response.data.permission) {
          setPermission(response.data.permission);
        }
        showSuccess(
          newValue
            ? 'Your child can now respond to match and training invites'
            : 'Your child can no longer respond to match and training invites'
        );
      } else {
        showError(response.data.message || 'Failed to update availability settings');
      }
    } catch (error) {
      console.error('Error updating child availability permission:', error);
      showError('An error occurred while updating availability settings');
    } finally {
      setIsLoading(false);
    }
  };

  const validateMobileNumber = (number) => validateUkMobile(number);

  const handleMobileNumberBlur = () => {
    const normalized = normalizeUkMobile(childMobileNumber);
    if (normalized) {
      setChildMobileNumber(`0${normalized.slice(0, 4)} ${normalized.slice(4)}`);
    }
  };

  if (!isParent) {
    return null;
  }

  return (
    <div className="child-access-settings-page container">
      <Header />
      <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      <h1>Child Access Settings</h1>
      <p className="page-description">
        Allow your child to log into their own account to view their profile, match results, and achievements. 
        This gives your child direct access to their player account.
      </p>

      <div className="settings-section">
        <h2>Allow Child to Access App</h2>
        
        <div className="permission-info">
          
          {permission && permission.permission_id ? (
            <>
              {permission.granted_at && (
                <p><strong>App Access:</strong> Granted by {permission.granted_by?.split(' ').map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()).join(' ')} on {new Date(permission.granted_at).toLocaleDateString('en-GB')}</p>
              )}
              {permission.revoked_at && (
                <p><strong>App Access:</strong> Revoked by {permission.revoked_by?.split(' ').map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()).join(' ')} on {new Date(permission.revoked_at).toLocaleDateString('en-GB')}</p>
              )}
            </>
          ) : (
            <div>
              <p>Toggle below and complete the form fields to allow your child access to their profile</p>
              <p>Once completed, you can share their login credentials with them.</p>
            </div>
          )}
        </div>
        
        <div className="toggle-container">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={allowChildAccess || showApprovalForm}
              onChange={handleToggleChange}
              disabled={isLoading || !apiResponse?.data?.can_manage_permissions}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">
              Allow {childName.split(' ')[0]} to access the app
            </span>
          </label>
          {!apiResponse?.data?.can_manage_permissions && (
            <p style={styles.toggleDisabledMessage}>
              Toggle disabled - you don't have permission to manage this child's access
            </p>
          )}
        </div>

        {(showApprovalForm || (allowChildAccess && !showApprovalForm)) && (
          <div className="settings-subsection availability-section">
            <div className="toggle-container">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={allowChildAvailability}
                  onChange={handleAvailabilityToggleChange}
                  disabled={isLoading || !apiResponse?.data?.can_manage_permissions}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  Allow {childName.split(' ')[0] || 'your child'} to respond to match and training invites
                </span>
              </label>
            </div>
          </div>
        )}

        {showApprovalForm && (
          <div className="approval-form">
            <label htmlFor="childMobile">Child's Mobile Number</label>
            <input
              type="tel"
              id="childMobile"
              value={childMobileNumber}
              onChange={handleMobileNumberChange}
              onBlur={handleMobileNumberBlur}
              placeholder="07xxx xxxxxx"
              className={childMobileNumber && !validateMobileNumber(childMobileNumber) ? 'error' : ''}
            />
            {childMobileNumber && !validateMobileNumber(childMobileNumber) && (
              <span className="error-message">Please enter a valid UK mobile number (e.g. 07700 900123)</span>
            )}
            
            <label htmlFor="childPassword">Child's Password</label>
            <input
              type="password"
              id="childPassword"
              value={childPassword}
              onChange={handlePasswordChange}
              placeholder="Enter password (min 6 characters)"
              className={childPassword && childPassword.length < 6 ? 'error' : ''}
            />
            {childPassword && childPassword.length < 6 && (
              <span className="error-message">Password must be at least 6 characters long</span>
            )}
            
            <button 
              type="button" 
              className="btn-approve"
              onClick={handleApproveAccess}
              disabled={isLoading || !childMobileNumber.trim() || !validateMobileNumber(childMobileNumber) || !childPassword.trim() || childPassword.length < 6}
            >
              {isLoading ? 'Approving...' : 'Approve Access'}
            </button>
          </div>
        )}

        {allowChildAccess && !showApprovalForm && (
          <div className="mobile-number-section">
            <label htmlFor="childMobile">Child's Mobile Number</label>
            <input
              type="tel"
              id="childMobile"
              value={childMobileNumber ? `0${childMobileNumber}` : ''}
              onChange={handleMobileNumberChange}
              placeholder="07xxx xxxxx"
              className={childMobileNumber && !validateMobileNumber(childMobileNumber) ? 'error' : ''}
              disabled={true}
            />
            <p className="help-text">
              This number is being used for your child's login credentials
            </p>
            <p className="help-text">
              Your child can now log in using their mobile number and the password you set
            </p>
          </div>
        )}

        {/* Disable Confirmation Modal */}
        {showDisableConfirmation && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>Confirm Disable Access</h2>
              <p>Are you sure you want to disable your child's access to the app?</p>
              <div className="modal-buttons">
                <button 
                  type="button" 
                  className="cancel"
                  onClick={() => setShowDisableConfirmation(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={handleConfirmedDisable}
                  disabled={isLoading}
                >
                  {isLoading ? 'Disabling...' : 'Yes, Disable Access'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="info-section">
        <h2>About Child Access</h2>
        <div className="info-content">
          <p>When you enable access for your child, they will be able to log into the app using their mobile number. Here's what your child will have access to:</p>
          
          <div className="capabilities-list">
            <h3>What your child can access:</h3>
            <ul>
              <li>View their own player profile and statistics</li>
              <li>See their match reports and performance updates</li>
              <li>View their achievements and awards</li>
              <li>Check training schedules and their attendance</li>
              <li>Read team announcements and communications</li>
              <li>View upcoming match fixtures and past results</li>
              <li>Access age-appropriate educational content</li>
            </ul>
          </div>

          <div className="limitations-list">
            <h3>What your child cannot do:</h3>
            <ul>
              <li>Edit their profile information</li>
              <li>Respond to match and training invites (only when you enable the availability toggle above)</li>
              <li>Make payments or access financial information</li>
              <li>Access team management features</li>
              <li>View other players' personal information</li>
              <li>Send messages or communicate with other users</li>
            </ul>
          </div>

          <div className="note-box">
            <p><strong>Important:</strong> Your child's access is read-only and designed to be safe and age-appropriate. You can revoke access at any time using this toggle. If you have any concerns about your child's use of the app, please contact your team manager.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentSettings; 